import { logger } from "@/utils/logger";
import React, { useState, useEffect, memo } from 'react';
import { ArrowRight, RotateCw } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { DealerManager } from '@/utils/dealerManager';
import { useCertificationStyles } from '@/hooks/useCertificationStyles';

interface TradeBannerProps {
  onClose: () => void;
  onSubmit: (data: any) => void;
  activeForm?: any;
  isOnHomePage?: boolean;
  isFormCollapsed?: boolean;
}

const TradeBanner: React.FC<TradeBannerProps> = ({ 
  onClose, 
  onSubmit, 
  activeForm = null,
  isOnHomePage = false,
  isFormCollapsed = false
}) => {
  const { t, language } = useLanguage();
  const { styles } = useCertificationStyles();
  const [isVisible, setIsVisible] = useState(true);
  
  // Debug: Check for TradeBoost elements on component mount
  useEffect(() => {}, []);
  
  // Hide trade banner when other forms are active
  useEffect(() => {
    if (activeForm) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  }, [activeForm]);

  const handleBannerClick = async () => {
    
    // Debug dealer state
    const currentDealer = DealerManager.getCurrentDealer();
    // Check if TradeBoost is enabled for this dealer
    const isTradeBoostEnabled = DealerManager.isTradeBoostEnabled();
    
    if (isTradeBoostEnabled) {
      const $ = (window as any).$;
      if ($) {
        try {
          const tradeContainer = $('#TradeInBarPopupContainer');
          if (tradeContainer.length > 0) {
            // Try different approaches to open the popup
            if (typeof tradeContainer.popupize === 'function') {
              const popupized = tradeContainer.popupize();
              
              // Check if setTrigger method exists
              if (typeof popupized.setTrigger === 'function') {
                popupized.setTrigger('click').open();
              } else if (typeof popupized.open === 'function') {
                // Try direct open without setTrigger
                popupized.open();
              } else {
                // Fallback: try click trigger directly
                tradeContainer.trigger('click');
              }
            } else {
              // Fallback: try click trigger directly
              tradeContainer.trigger('click');
            }
          } else {
            throw new Error('TradeInBarPopupContainer not found');
          }
        } catch (error) {
          // Fallback to URL redirect on any error
          window.location.href = language === 'fr' 
            ? '/demandes/vehicule-echange-occasion.html'
            : '/requests/trade-in-used.html';
        }
      } else {
        // jQuery not available, redirect
        window.location.href = language === 'fr' 
          ? '/demandes/vehicule-echange-occasion.html'
          : '/requests/trade-in-used.html';
      }
      return;
    }
    
    // Add a small delay before redirecting to allow any pending operations to complete
    logger.log('⏳ Adding delay before external redirect to prevent session corruption...');
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Fallback: Redirect to trade-in page based on language
    logger.log('📍 Falling back to URL redirect');
    const redirectUrl = language === 'fr' 
      ? '/demandes/vehicule-echange-occasion.html'
      : '/requests/trade-in-used.html';
    
    logger.log('🔗 Redirecting to:', redirectUrl);
    window.location.href = redirectUrl;
  };

  // Only hide if there's an active form
  if (activeForm || !isVisible) {
    return null;
  }

  return (
    <div className="trade-banner-container d2cbot-bg-white d2cbot-bg-gradient-none">
      <div 
        className="trade-banner-card d2cbot-bg-white d2cbot-bg-gradient-none"
        style={{ borderRadius: `${styles.corner_radius}px` }}
      >
        <button
          className="trade-banner-button"
          onClick={handleBannerClick}
        >
          <div className="trade-banner-content">
            <div className="trade-banner-icon-container">
              <RotateCw className="trade-banner-icon" />
            </div>
            <span className="trade-banner-text">
              {t('trade.estimate.title')}
            </span>
            <ArrowRight className="trade-banner-arrow" />
          </div>
        </button>
      </div>
    </div>
  );
};

export default memo(TradeBanner);